// Shared color scale, legend, and formatting for the U.S. map (AnomalyMap) and
// the city map (CityMap), so both read the same way.
import * as d3 from "d3";

// ── Color ─────────────────────────────────────────────────────────────────────
// Diverging scale: blue (cooler than normal) → near-white → red (hotter).
// d3.interpolateRdBu runs red→blue, so the domain is reversed.

export const SCALE_MAX = 15; // °F at the ends of the scale; wide enough for heat waves
export const anomalyColor = d3.scaleDiverging(d3.interpolateRdBu).domain([SCALE_MAX, 0, -SCALE_MAX]).clamp(true);
export const LEGEND_STOPS = [-15, -10, -5, 0, 5, 10, 15];

// 256-step lookup table for painting pixels quickly.
export const COLOR_LUT = d3.range(256).map((i) => {
  const c = d3.rgb(anomalyColor(-SCALE_MAX + (i / 255) * 2 * SCALE_MAX));
  return [c.r, c.g, c.b];
});

// Anomalies arrive in °F. A temperature *difference* converts by ÷1.8 (no offset).
export function formatAnomaly(valF, unit = "F") {
  const val = unit === "C" ? valF / 1.8 : valF;
  const sign = val > 0 ? "+" : "";
  return `${sign}${val.toFixed(1)}°${unit}`;
}

// ── Legend ────────────────────────────────────────────────────────────────────

// Colors are always on the ±15 °F scale; labels follow the page's unit.
export function MapLegend({ unit = "F" }) {
  const gradientId = "anomaly-gradient";
  const stops = d3
    .range(0, 1.01, 0.01)
    .map((t) => ({ offset: `${Math.round(t * 100)}%`, color: anomalyColor(-SCALE_MAX + t * 2 * SCALE_MAX) }));

  return (
    <div className="map-legend">
      <svg width="180" height="12" aria-hidden="true">
        <defs>
          <linearGradient id={gradientId} x1="0" x2="1" y1="0" y2="0">
            {stops.map((s) => (
              <stop key={s.offset} offset={s.offset} stopColor={s.color} />
            ))}
          </linearGradient>
        </defs>
        <rect x="0" y="0" width="180" height="12" fill={`url(#${gradientId})`} rx="2" />
      </svg>
      <div className="map-legend-labels">
        {LEGEND_STOPS.map((v) => (
          <span key={v} className={v > 0 ? "legend-hot" : v < 0 ? "legend-cool" : ""}>
            {(() => {
              const shown = unit === "C" ? Math.round(v / 1.8) : v;
              return shown > 0 ? `+${shown}` : shown;
            })()}°
          </span>
        ))}
      </div>
      <p className="map-legend-caption">°{unit} cooler ← than 1951–80 → °{unit} hotter</p>
    </div>
  );
}
